import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Building2, 
  UserCheck, 
  UserX,
  Loader2,
  Eye,
  EyeOff,
  Activity,
  Upload,
  Link,
  BookOpen,
  Copy,
  Package,
  CalendarClock,
} from 'lucide-react';
import { adminApi } from '../../utils/admin-api';
import { API_BASE } from '../../utils/api-base';
import { sessionStorage } from '../../utils/auth-api';
import { toast } from 'sonner';
import { OneDriveScheduleDialog } from './OneDriveScheduleDialog';

const DEFAULT_CLIENT_MODULES = {
  energyConsumption: true,
  manualLogging: true,
  livePlcData: true,
  offlineData: true,
  scrapManagement: true,
  supportTickets: true,
  settings: true,
  scanners: true,
  waveshare8ch: false,
  externalMonitor: false,
  plantKpi: false,
  maintenanceScheduler: false,
  teamActivity: false,
  smartAlerts: false,
  automatedReports: false,
  energyForecast: false,
  chatBot: false,
  copilot: false,
};

const DEFAULT_CLIENT_WIDGETS = {
  barChart: true,
  lineChart: true,
  pieChart: true,
  gaugeChart: true,
  customizableDashboard: true,
};

const DEFAULT_THEME_CONFIG = {
  primaryColor: '#3b82f6',
  sidebarStyle: 'default',
  dashboardLayout: 'modern',
};

const CLIENT_MODULE_LABELS = {
  energyConsumption: 'Energy Consumption',
  manualLogging: 'Manual Logging',
  livePlcData: 'Live PLC Data',
  offlineData: 'Offline Data',
  scrapManagement: 'Scrap Management',
  supportTickets: 'Support Tickets',
  settings: 'Settings',
  scanners: 'Scanners',
  waveshare8ch: 'Waveshare Modbus RTU IO 8CH',
  externalMonitor: 'External Monitor',
  plantKpi: 'Plant KPI Dashboard',
  maintenanceScheduler: 'Maintenance Scheduler',
  teamActivity: 'Team Activity (Presence & Reporting)',
  smartAlerts: 'Smart Alerts & Root Cause Hints',
  automatedReports: 'Automated Report Generation',
  energyForecast: 'Energy Consumption Forecasting',
  chatBot: 'Chat Bot (rule-based Q&A)',
  copilot: 'Operations Copilot (AI, grounded in client data)',
};

const CLIENT_WIDGET_LABELS = {
  barChart: 'Bar Chart',
  lineChart: 'Line Chart',
  pieChart: 'Pie Chart',
  gaugeChart: 'Gauge Chart',
  customizableDashboard: 'Allow User Customization',
};

const MAC_ADDRESS_REGEX = /^([0-9A-F]{2}[:-]){5}([0-9A-F]{2})$/i;

const normalizeMacAddress = (value: string) => value.trim().replace(/-/g, ':').toUpperCase();

const normalizeMacAddressList = (value: string) => {
  const rawEntries = String(value || '')
    .split(/[\n,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

  const uniqueEntries = Array.from(new Set(rawEntries.map((entry) => normalizeMacAddress(entry))));
  const invalidEntries = uniqueEntries.filter((entry) => !MAC_ADDRESS_REGEX.test(entry));

  return {
    valid: invalidEntries.length === 0,
    invalidEntries,
    normalizedEntries: uniqueEntries,
    normalizedValue: uniqueEntries.join(', '),
  };
};

const BRIDGE_INSTALL_STEPS = [
  'Open Super Admin > Clients and edit the target client.',
  'In MAC Addresses, keep existing MACs and add new machine MACs (comma or new line separated).',
  'Click Download Full Bridge Kit for that client.',
  'Copy zip to target machine and extract it.',
  'Run install-bridge-service.bat as Administrator.',
  'Run start-bridge-service.bat.',
  'Verify logs folder and Bridge Registry status becomes online.',
];

const BRIDGE_GATEWAY_NOTES = [
  'Bridge can run on non-client PC only if that machine has actual network/serial access to PLC or meter.',
  'Gateway machine MAC must be added in the same client MAC list.',
  'If machine has no PLC/COM connectivity, bridge will start but data will not flow.',
];

const CONNECTIVITY_STATUS_STYLES: Record<string, string> = {
  healthy: 'bg-green-500/10 text-green-500 border-green-500/20',
  not_configured: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  disabled: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  offline: 'bg-red-500/10 text-red-500 border-red-500/20',
  online_no_fresh_data: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
};

export function ClientsTab() {
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingBridgeClientId, setTestingBridgeClientId] = useState<string | null>(null);
  const [downloadingBridgeClientId, setDownloadingBridgeClientId] = useState<string | null>(null);
  const [downloadingSetupClientId, setDownloadingSetupClientId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [connectivityDialogOpen, setConnectivityDialogOpen] = useState(false);
  const [connectivityReport, setConnectivityReport] = useState<any | null>(null);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [logoUploadMethod, setLogoUploadMethod] = useState<'url' | 'file'>('url');
  const [oneDriveClientId, setOneDriveClientId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    logo: '',
    logoSize: 40,
    username: '',
    password: '',
    macAddress: '',
    licenseKey: '',
    licenseExpiresAt: '',
    pollingIntervalMs: 5000,
    dataRetentionDays: 0,
    isActive: true,
    simulationMode: false,
    modules: DEFAULT_CLIENT_MODULES,
    widgets: DEFAULT_CLIENT_WIDGETS,
    themeConfig: DEFAULT_THEME_CONFIG,
  });

  const getSessionToken = () => {
    const session = sessionStorage.get();
    return session?.token || null;
  };

  const generateLicenseKey = () => {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    const key = `LK-${timestamp}-${random}`;
    setFormData({ ...formData, licenseKey: key });
    toast.success('License key generated');
  };

  useEffect(() => {
    loadClients();
  }, []);

  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const token = getSessionToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }

      console.log('Testing local API server connection...');
      const response = await fetch(`${API_BASE}/api/health`, {
        method: 'GET',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'API test failed');
      }

      toast.success('API server connection successful');
    } catch (error: any) {
      console.error('API test error:', error);
      toast.error(`Connection test failed: ${error.message}`);
    } finally {
      setTestingConnection(false);
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const token = getSessionToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }

      const data = await adminApi.getClients(token);
      setClients(data);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.username || !formData.email || (!editingClient && !formData.password)) {
      toast.error('Please fill in all required fields (Name, Email, Username, and Password)');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Invalid email format');
      return;
    }

    const macAddressResult = normalizeMacAddressList(formData.macAddress);
    if (!macAddressResult.valid) {
      toast.error(`Invalid MAC format: ${macAddressResult.invalidEntries.join(', ')}`);
      return;
    }

    try {
      const token = getSessionToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }

      const normalizedMacAddress = macAddressResult.normalizedValue;

      if (editingClient) {
        // Only send fields that are not empty, null, or undefined
        const updates = Object.fromEntries(
          Object.entries(formData).filter(([k, v]) =>
            v !== undefined && v !== null && v !== '' && !(k === 'password' && !v)
          )
        );
        // Convert camelCase to snake_case for API
        const apiUpdates: any = {};
        Object.entries(updates).forEach(([key, value]) => {
          if (key === 'macAddress') {
            apiUpdates.mac_address = normalizedMacAddress;
          } else if (key === 'licenseKey') {
            apiUpdates.license_key = value;
          } else if (key === 'licenseExpiresAt') {
            // Convert date string to ISO timestamp
            apiUpdates.license_expires_at = value && typeof value === 'string' ? new Date(value).toISOString() : null;
          } else if (key === 'pollingIntervalMs') {
            apiUpdates.polling_interval_ms = value;
          } else if (key === 'dataRetentionDays') {
            apiUpdates.data_retention_days = value;
          } else if (key === 'isActive') {
            apiUpdates.is_active = value;
          } else if (key === 'logoSize') {
            apiUpdates.logo_size = value;
          } else {
            apiUpdates[key] = value;
          }
        });
        // Stringify modules and widgets if present
        if (apiUpdates.modules && typeof apiUpdates.modules === 'object') {
          apiUpdates.modules = JSON.stringify(apiUpdates.modules);
        }
        if (apiUpdates.widgets && typeof apiUpdates.widgets === 'object') {
          apiUpdates.widgets = JSON.stringify(apiUpdates.widgets);
        }
        if (apiUpdates.themeConfig && typeof apiUpdates.themeConfig === 'object') {
          apiUpdates.theme_config = JSON.stringify(apiUpdates.themeConfig);
          delete apiUpdates.themeConfig; // Remove camelCase version
        }
        await adminApi.updateClient(token, editingClient.id, apiUpdates);
        toast.success('Client updated successfully');
      } else {
        // Convert camelCase to snake_case for API
        const apiData: any = {};
        Object.entries(formData).forEach(([key, value]) => {
          if (key === 'macAddress') {
            apiData.mac_address = normalizedMacAddress;
          } else if (key === 'licenseKey') {
            apiData.license_key = value;
          } else if (key === 'licenseExpiresAt') {
            // Convert date string to ISO timestamp
            apiData.license_expires_at = value && typeof value === 'string' ? new Date(value).toISOString() : null;
          } else if (key === 'pollingIntervalMs') {
            apiData.polling_interval_ms = value;
          } else if (key === 'dataRetentionDays') {
            apiData.data_retention_days = value;
          } else if (key === 'isActive') {
            apiData.is_active = value;
          } else if (key === 'logoSize') {
            apiData.logo_size = value;
          } else {
            apiData[key] = value;
          }
        });
        // Stringify modules, widgets, and themeConfig for database storage
        if (apiData.modules && typeof apiData.modules === 'object') {
          apiData.modules = JSON.stringify(apiData.modules);
        }
        if (apiData.widgets && typeof apiData.widgets === 'object') {
          apiData.widgets = JSON.stringify(apiData.widgets);
        }
        if (apiData.themeConfig && typeof apiData.themeConfig === 'object') {
          apiData.theme_config = JSON.stringify(apiData.themeConfig);
          delete apiData.themeConfig; // Remove camelCase version
        }
        await adminApi.createClient(token, apiData);
        toast.success('Client created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      loadClients();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (clientId: string) => {
    if (!confirm('Are you sure you want to delete this client? This will delete all associated PLCs, meters, and data.')) {
      return;
    }

    try {
      const token = getSessionToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }

      await adminApi.deleteClient(token, clientId);
      toast.success('Client deleted successfully');
      loadClients();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDownloadBridgeKit = async (client: any) => {
    try {
      setDownloadingBridgeClientId(client.id);
      const token = getSessionToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }

      const response = await fetch(`${API_BASE}/api/admin/clients/${client.id}/bridge-kit`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Failed to download bridge kit');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `bridge-kit-${(client.name || client.id).replace(/[^a-z0-9_-]+/gi, '-').toLowerCase()}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast.success('Full bridge kit downloaded');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download bridge kit');
    } finally {
      setDownloadingBridgeClientId(null);
    }
  };

  const handleDownloadBridgeSetup = async (client: any) => {
    try {
      setDownloadingSetupClientId(client.id);
      const token = getSessionToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }

      const response = await fetch(`${API_BASE}/api/admin/clients/${client.id}/bridge-setup`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Failed to download bridge setup file');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `bridge-setup-${(client.name || client.id).replace(/[^a-z0-9_-]+/gi, '-').toLowerCase()}.env`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast.success('Bridge setup file downloaded');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download bridge setup file');
    } finally {
      setDownloadingSetupClientId(null);
    }
  };

  const handleTestBridgeConnectivity = async (client: any) => {
    try {
      setTestingBridgeClientId(client.id);
      const token = getSessionToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }

      const response = await fetch(`${API_BASE}/api/admin/clients/${client.id}/bridge-connectivity`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to test bridge connectivity');
      }

      setConnectivityReport(payload);
      setConnectivityDialogOpen(true);
      toast.success(payload?.summary || 'Bridge connectivity check complete');
    } catch (error: any) {
      toast.error(error.message || 'Failed to test bridge connectivity');
    } finally {
      setTestingBridgeClientId(null);
    }
  };

  const handleEdit = (client: any) => {
    setEditingClient(client);
    // Format date for input (YYYY-MM-DD)
    const expiresAt = client.license_expires_at ? new Date(client.license_expires_at).toISOString().split('T')[0] : '';
    setFormData({
      name: client.name || '',
      email: client.email || '',
      logo: client.logo || '',
      logoSize: Number(client.logoSize) || 40,
      username: client.username || '',
      password: '', // Don't show password
      macAddress: client.mac_address || '',
      licenseKey: client.license_key || '',
      licenseExpiresAt: expiresAt,
      pollingIntervalMs: Number(client.pollingIntervalMs) || 5000,
      dataRetentionDays: Number(client.dataRetentionDays) || 0,
      isActive: client.isActive !== false,
      simulationMode: !!client.simulationMode,
      modules: {
        ...DEFAULT_CLIENT_MODULES,
        ...(client.modules || {}),
      },
      widgets: {
        ...DEFAULT_CLIENT_WIDGETS,
        ...(client.widgets || {})
      },
      themeConfig: {
        ...DEFAULT_THEME_CONFIG,
        ...(client.themeConfig || client.theme_config || {}),
      },
    });
    // Detect logo upload method
    setLogoUploadMethod(client.logo && String(client.logo).startsWith('data:') ? 'file' : 'url');
    setDialogOpen(true);
  };

  const handleToggleActive = async (client: any) => {
    try {
      const token = getSessionToken();
      if (!token) {
        throw new Error('Session expired. Please log in again.');
      }

      await adminApi.updateClient(token, client.id, {
        ...client,
        isActive: !client.isActive,
      });
      toast.success(`Client ${!client.isActive ? 'activated' : 'deactivated'}`);
      loadClients();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      email: '',
      logo: '',
      logoSize: 40,
      username: '',
      password: '',
      macAddress: '',
      licenseKey: '',
      licenseExpiresAt: '',
      pollingIntervalMs: 5000,
      dataRetentionDays: 0,
      isActive: true,
      simulationMode: false,
      modules: DEFAULT_CLIENT_MODULES,
      widgets: DEFAULT_CLIENT_WIDGETS,
      themeConfig: DEFAULT_THEME_CONFIG,
    });
    setShowPassword(false);
    setLogoUploadMethod('url');
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const copyBridgeGuide = async () => {
    try {
      const content = [
        'FACTREON BRIDGE INSTALL GUIDE',
        '',
        'STEP-BY-STEP:',
        ...BRIDGE_INSTALL_STEPS.map((step, index) => `${index + 1}. ${step}`),
        '',
        'NON-CLIENT-PC (GATEWAY) NOTES:',
        ...BRIDGE_GATEWAY_NOTES.map((note, index) => `${index + 1}. ${note}`),
      ].join('\n');
      await navigator.clipboard.writeText(content);
      toast.success('Bridge install guide copied');
    } catch {
      toast.error('Failed to copy guide. Please copy manually from this dialog.');
    }
  };

  const formatSecondsAgo = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return 'Never';
    if (seconds < 60) return `${seconds}s ago`;
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${mins % 60}m ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h ago`;
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      <Dialog open={connectivityDialogOpen} onOpenChange={setConnectivityDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bridge Connectivity Result</DialogTitle>
            <DialogDescription>
              {connectivityReport?.client?.name || 'Client bridge connectivity summary'}
            </DialogDescription>
          </DialogHeader>

          {connectivityReport && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg border p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-semibold ${CONNECTIVITY_STATUS_STYLES[connectivityReport.status] || 'bg-muted text-foreground border-border'}`}>
                    {String(connectivityReport.status || 'unknown').replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-muted-foreground">Generated: {new Date(connectivityReport.generated_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-muted-foreground">{connectivityReport.summary}</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">Online Bridges</p>
                  <p className="text-lg font-semibold">{connectivityReport.counts?.online_bridges ?? 0}</p>
                </div>
                <div className="rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">Active Bridges</p>
                  <p className="text-lg font-semibold">{connectivityReport.counts?.active_bridges ?? 0}</p>
                </div>
                <div className="rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">Active PLCs</p>
                  <p className="text-lg font-semibold">{connectivityReport.counts?.active_plcs ?? 0}</p>
                </div>
                <div className="rounded-md border p-2">
                  <p className="text-xs text-muted-foreground">Active Meters</p>
                  <p className="text-lg font-semibold">{connectivityReport.counts?.active_meters ?? 0}</p>
                </div>
              </div>

              <div className="rounded-lg border p-3 space-y-2">
                <h4 className="font-semibold">Data Freshness</h4>
                <p className="text-xs text-muted-foreground">Latest data: {connectivityReport.data_flow?.latest_any_data_at ? new Date(connectivityReport.data_flow.latest_any_data_at).toLocaleString() : 'No data yet'}</p>
                <p className="text-xs text-muted-foreground">Age: {formatSecondsAgo(connectivityReport.data_flow?.seconds_since_latest_data)}</p>
              </div>

              <div className="rounded-lg border p-3 space-y-2">
                <h4 className="font-semibold">Bridge Status</h4>
                <div className="space-y-2">
                  {(connectivityReport.bridges || []).length === 0 && (
                    <p className="text-xs text-muted-foreground">No bridge found for this client.</p>
                  )}
                  {(connectivityReport.bridges || []).map((bridge: any) => (
                    <div key={bridge.bridge_id} className="rounded-md bg-muted/40 p-2">
                      <p className="font-medium text-xs">{bridge.bridge_id}</p>
                      <p className="text-xs text-muted-foreground">MAC: {bridge.mac_address || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">State: {String(bridge.state || 'unknown').replace(/_/g, ' ')}</p>
                      <p className="text-xs text-muted-foreground">Last seen: {formatSecondsAgo(bridge.seconds_since_last_seen)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {(connectivityReport.recommendations || []).length > 0 && (
                <div className="rounded-lg border p-3 space-y-2">
                  <h4 className="font-semibold">Recommended Actions</h4>
                  <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground">
                    {(connectivityReport.recommendations || []).map((item: string) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Client Management</h2>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Manage client accounts and configure their access
          </p>
        </div>
        
        <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-nowrap">
          <Button
            variant="outline"
            onClick={testConnection}
            disabled={testingConnection}
            className="gap-2 flex-1 sm:flex-none"
          >
            {testingConnection ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Activity className="h-4 w-4" />
            )}
            Test Connection
          </Button>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2 flex-1 sm:flex-none">
                <BookOpen className="h-4 w-4" />
                Bridge Install Guide
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Bridge Install Guide</DialogTitle>
                <DialogDescription>
                  Follow these steps for client PC and gateway installs.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 text-sm">
                <div className="space-y-2">
                  <h4 className="font-semibold">Step-by-step</h4>
                  <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground">
                    {BRIDGE_INSTALL_STEPS.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                </div>

                <div className="space-y-2 rounded-lg border bg-muted/40 p-3">
                  <h4 className="font-semibold">If not installing on client PC</h4>
                  <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground">
                    {BRIDGE_GATEWAY_NOTES.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ol>
                </div>

                <div className="flex justify-end">
                  <Button type="button" variant="outline" className="gap-2" onClick={copyBridgeGuide}>
                    <Copy className="h-4 w-4" />
                    Copy Steps
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                <Plus className="h-4 w-4" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
                <DialogDescription>
                  Configure client details, credentials, and module access
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Basic Information</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Company Name *</Label>
                    <Input
                      id="name"
                      value={formData.name ?? ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Acme Corporation"
                    />
                    <p className="text-xs text-muted-foreground">This name will appear in the client portal header</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email ?? ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@company.com"
                    />
                    <p className="text-xs text-muted-foreground">Email address to receive license expiration notifications and system alerts</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Logo</Label>
                    <div className="flex gap-2 mb-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={logoUploadMethod === 'url' ? 'default' : 'outline'}
                        onClick={() => setLogoUploadMethod('url')}
                        className="flex-1 gap-2"
                      >
                        <Link className="h-4 w-4" />
                        URL
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={logoUploadMethod === 'file' ? 'default' : 'outline'}
                        onClick={() => setLogoUploadMethod('file')}
                        className="flex-1 gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        Upload File
                      </Button>
                    </div>
                    
                    {logoUploadMethod === 'file' ? (
                      <Input
                        key="logo-file"
                        id="logo-file"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.currentTarget.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setFormData({ ...formData, logo: reader.result as string });
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="cursor-pointer"
                      />
                    ) : (
                      <Input
                        key="logo-url"
                        id="logo"
                        type="text"
                        value={formData.logo ?? ''}
                        onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                        placeholder="https://example.com/logo.png"
                      />
                    )}
                    <p className="text-xs text-muted-foreground">This logo will appear in the client portal header</p>
                    {formData.logo && (
                      <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                        <img 
                          src={formData.logo} 
                          alt="Logo preview" 
                          className="rounded-lg object-cover"
                          style={{ width: `${formData.logoSize}px`, height: `${formData.logoSize}px` }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <span className="text-xs text-muted-foreground flex-1">Logo Preview ({formData.logoSize}px)</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setFormData({ ...formData, logo: '' })}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="logoSize">Logo Size (pixels)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="logoSize"
                        type="number"
                        min="20"
                        max="200"
                        value={formData.logoSize}
                        onChange={(e) => setFormData({ ...formData, logoSize: parseInt(e.target.value) || 40 })}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">Recommended: 32-64px</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isActive"
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    />
                    <Label htmlFor="isActive">Active (Allow Login)</Label>
                  </div>

                  <div className="space-y-2 border border-amber-500/30 bg-amber-500/5 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="simulationMode"
                        checked={formData.simulationMode}
                        onCheckedChange={(checked) => setFormData({ ...formData, simulationMode: checked })}
                      />
                      <Label htmlFor="simulationMode" className="font-semibold">Simulation / Demo Mode</Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Generates realistic auto-updating demo data (every 5s) across Energy Meters, Live PLC Data,
                      Scanners, and HMI Mimic for this client — for sales demos only. Never enable on a client with
                      a real bridge connected.
                    </p>
                  </div>
                </div>

                {/* Credentials */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Login Credentials</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="username">Username *</Label>
                    <Input
                      id="username"
                      value={formData.username ?? ''}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder="clientusername"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">
                      Password {!editingClient && '*'}
                      {editingClient && <span className="text-xs text-muted-foreground ml-2">(leave blank to keep current)</span>}
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password ?? ''}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter secure password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* MAC Addresses */}
                  <div className="space-y-2">
                    <Label htmlFor="macAddress">MAC Addresses (Device Identifiers)</Label>
                    <Textarea
                      id="macAddress"
                      value={formData.macAddress ?? ''}
                      onChange={(e) => setFormData({ ...formData, macAddress: e.target.value })}
                      rows={3}
                      placeholder={`e.g.,\n00:1A:2B:3C:4D:5E\n00:1A:2B:3C:4D:5F`}
                    />
                    <p className="text-xs text-muted-foreground">Add one or multiple MAC addresses (comma/new line separated) for all bridge PCs of this client.</p>
                  </div>

                  {/* License Key */}
                  <div className="space-y-2">
                    <Label htmlFor="licenseKey">License Key</Label>
                    <div className="flex gap-2">
                      <Input
                        id="licenseKey"
                        value={formData.licenseKey ?? ''}
                        onChange={(e) => setFormData({ ...formData, licenseKey: e.target.value })}
                        placeholder="Auto-generated or custom key"
                        readOnly={!editingClient}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={generateLicenseKey}
                        className="flex-shrink-0"
                      >
                        Generate
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Unique license identifier for this client. Auto-generated if left empty.</p>
                  </div>

                  {/* License Expiration Date */}
                  <div className="space-y-2">
                    <Label htmlFor="licenseExpiresAt">License Expires On</Label>
                    <Input
                      id="licenseExpiresAt"
                      type="date"
                      value={formData.licenseExpiresAt ?? ''}
                      onChange={(e) => setFormData({ ...formData, licenseExpiresAt: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">Dashboard will be fully disabled after this date. Set a future date to extend the license.</p>
                  </div>

                  {/* Polling Interval */}
                  <div className="space-y-2">
                    <Label htmlFor="pollingIntervalMs">PLC Polling Interval (milliseconds)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        id="pollingIntervalMs"
                        type="number"
                        min="1000"
                        max="3600000"
                        step="1000"
                        value={formData.pollingIntervalMs}
                        onChange={(e) => setFormData({ ...formData, pollingIntervalMs: parseInt(e.target.value) || 5000 })}
                        className="flex-1"
                      />
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {formData.pollingIntervalMs >= 60000
                          ? `${(formData.pollingIntervalMs / 60000).toFixed(1)} min`
                          : `${(formData.pollingIntervalMs / 1000).toFixed(1)}s`}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">How frequently the bridge polls PLCs for data. Min: 1 second, Max: 60 minutes. Default: 5 seconds.</p>
                  </div>

                  {/* Historical Data Retention */}
                  <div className="space-y-2">
                    <Label htmlFor="dataRetentionDays">Historical Data Retention (days)</Label>
                    <Input
                      id="dataRetentionDays"
                      type="number"
                      min="0"
                      step="1"
                      value={formData.dataRetentionDays}
                      onChange={(e) => setFormData({ ...formData, dataRetentionDays: Math.max(0, parseInt(e.target.value) || 0) })}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.dataRetentionDays > 0
                        ? `Historical readings and energy meter readings older than ${formData.dataRetentionDays} day(s) are purged automatically.`
                        : '0 = keep forever (default). Older readings are never automatically deleted.'}
                    </p>
                  </div>
                </div>

                {/* Module Access */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Module Access</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(CLIENT_MODULE_LABELS).map(([key, label]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <Checkbox
                          id={key}
                          checked={!!formData.modules[key as keyof typeof formData.modules]}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              modules: { ...formData.modules, [key]: checked === true }
                            })
                          }
                        />
                        <Label htmlFor={key} className="cursor-pointer">{label}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Widget Access */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Available Widgets</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(CLIENT_WIDGET_LABELS).map(([key, label]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <Checkbox
                          id={`widget-${key}`}
                          checked={formData.widgets[key as keyof typeof formData.widgets]}
                          onCheckedChange={(checked) => 
                            setFormData({
                              ...formData,
                              widgets: { ...formData.widgets, [key]: checked === true }
                            })
                          }
                        />
                        <Label htmlFor={`widget-${key}`} className="cursor-pointer">{label}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Theme Config (Optional for Super Admin) */}
                <div className="space-y-4 p-4 border border-blue-500/20 rounded-lg bg-blue-500/5">
                  <h3 className="font-semibold text-lg text-blue-400">Default Theme</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <Input 
                        type="color" 
                        value={formData.themeConfig.primaryColor}
                        onChange={(e) => setFormData({
                          ...formData,
                          themeConfig: { ...formData.themeConfig, primaryColor: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sidebar Style</Label>
                      <select
                        className="w-full h-10 px-3 py-2 text-sm rounded-md border border-input bg-background"
                        value={formData.themeConfig.sidebarStyle}
                        onChange={(e) => setFormData({
                          ...formData,
                          themeConfig: { ...formData.themeConfig, sidebarStyle: e.target.value }
                        })}
                      >
                        <option value="default">Default</option>
                        <option value="minimal">Minimal</option>
                        <option value="dark">Dark</option>
                        <option value="glass">Glass</option>
                      </select>
                    </div>
                  </div>
                </div>

                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                  <Button type="submit" className="flex-1">
                    {editingClient ? 'Update Client' : 'Create Client'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Clients List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No clients yet</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Get started by adding your first client
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {clients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="relative overflow-hidden group hover:shadow-lg transition-all">
                {/* Active Status Badge */}
                <div className="absolute top-3 right-3 z-10">
                  {client.isActive ? (
                    <div className="flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-500 rounded-full text-xs">
                      <UserCheck className="h-3 w-3" />
                      Active
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-500 rounded-full text-xs">
                      <UserX className="h-3 w-3" />
                      Inactive
                    </div>
                  )}
                </div>

                <CardHeader>
                  <div className="flex items-start gap-3">
                    {client.logo ? (
                      <img src={client.logo} alt={client.name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <CardTitle className="text-lg">{client.name}</CardTitle>
                      <CardDescription className="text-xs">@{client.username}</CardDescription>
                      {client.email && (
                        <CardDescription className="text-xs mt-1">
                          <span className="text-muted-foreground">📧 {client.email}</span>
                        </CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {/* Module Access */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Modules</p>
                    <div className="flex max-h-20 overflow-y-auto flex-wrap gap-1 pr-1">
                      {Object.entries(client.modules || {})
                        .filter(([_, enabled]) => enabled)
                        .slice(0, 6)
                        .map(([key]) => (
                          <span key={key} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                        ))}
                      {Object.entries(client.modules || {}).filter(([_, enabled]) => enabled).length > 6 && (
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                          +more
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>MACs: {client.mac_address || 'Not set'}</p>
                    <p>Polling: {client.pollingIntervalMs || 5000} ms</p>
                    <p>Data retention: {client.dataRetentionDays > 0 ? `${client.dataRetentionDays} day(s)` : 'Forever'}</p>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button
                      size="sm"
                      className="col-span-2"
                      onClick={() => handleDownloadBridgeKit(client)}
                      disabled={downloadingBridgeClientId === client.id}
                    >
                      {downloadingBridgeClientId === client.id ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <Package className="h-3.5 w-3.5 mr-1" />
                      )}
                      {downloadingBridgeClientId === client.id ? 'Preparing Kit...' : 'Download Full Bridge Kit'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="col-span-2"
                      onClick={() => handleDownloadBridgeSetup(client)}
                      disabled={downloadingSetupClientId === client.id}
                    >
                      {downloadingSetupClientId === client.id ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <BookOpen className="h-3.5 w-3.5 mr-1" />
                      )}
                      {downloadingSetupClientId === client.id ? 'Preparing Setup...' : 'Download Bridge Setup (.env)'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="col-span-2"
                      onClick={() => setOneDriveClientId(client.id)}
                    >
                      <CalendarClock className="h-3 w-3 mr-1" />
                      Report Schedules
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="col-span-2"
                      onClick={() => handleTestBridgeConnectivity(client)}
                      disabled={testingBridgeClientId === client.id}
                    >
                      {testingBridgeClientId === client.id ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <Activity className="h-3 w-3 mr-1" />
                      )}
                      Test Bridge Connectivity
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => handleEdit(client)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant={client.isActive ? "outline" : "default"}
                      className="w-full"
                      onClick={() => handleToggleActive(client)}
                    >
                      {client.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="w-full"
                      onClick={() => handleDelete(client.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {oneDriveClientId && (
        <OneDriveScheduleDialog
          clientId={oneDriveClientId}
          clientName={clients.find((c) => c.id === oneDriveClientId)?.name || oneDriveClientId}
          open={!!oneDriveClientId}
          onClose={() => setOneDriveClientId(null)}
        />
      )}
    </div>
  );
}
